import React from "react";
import { useRouter } from "next/router";
import { useSessionKf } from "components/auth/lib/useSessionKf";
import UserCircle from "components/icons/24/other/user-circle.svg";
import ProjectSvg from "components/icons/24/workspace_project.svg";
import SupportSvg from "components/icons/24/other/support.svg";
import ExternalLinkSvg from "components/icons/24/actions/action-external-link.svg";
import LogoutSvg from "components/icons/24/actions/logout.svg";
import DropdownSimple from "components/elements/Dropdown/DropdownSimple";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCurrentProject } from "lib/hooks/useProject";
import { useProjectMembers } from "lib/hooks/useProjectMembers";
import { ProjectMemberPermissions } from "lib/types/members/ProjectMemberPermissions";

const projectRoleLabels: Partial<Record<ProjectMemberPermissions, string>> = {
  [ProjectMemberPermissions.viewer]: "Viewer",
  [ProjectMemberPermissions.contributor]: "Contributor",
  [ProjectMemberPermissions.admin]: "Admin",
};

const settingsButtonClassName =
  "shrink-0 rounded-full border border-neutral-300 px-3 py-1 text-xs text-secondary outline-none hover:border-neutral-400 hover:text-neutral-950 focus-visible:border-neutral-950";

const rowClassName =
  "flex w-full items-center gap-3 rounded px-3 py-2 text-left text-sm text-secondary outline-none hover:bg-gray-50 hover:text-neutral-950 focus-visible:bg-gray-50";

const AccountDropdown = (): React.JSX.Element => {
  const router = useRouter();

  const {
    session: {
      user: { given_name: givenName, family_name: familyName, name, email },
    },
  } = useSessionKf();

  const accountButtonContent = givenName ? `${givenName} ${familyName}` : name;
  const { featureAccountSettings: hasAccountSettings } = useFlags();

  // Outside a project there is no project ID, so no project or members are
  // fetched and the project section is hidden.
  const { projectId, project } = useCurrentProject();
  const { members } = useProjectMembers(projectId);
  const myPermissions = members?.find((member) => member.isMe)?.permissions;
  const projectRoleLabel = myPermissions && projectRoleLabels[myPermissions];

  async function onSettings() {
    router.push(`/accounts/settings`);
  }

  function onProjectSettings() {
    router.push(`/projects/${projectId}/settings`);
  }

  function onContactSupport() {
    window.open(process.env.NEXT_PUBLIC_SUPPORT_FORM_URL);
  }

  return (
    <DropdownSimple
      dropdownWidth={320}
      variant="simple"
      className="flex items-center justify-center self-stretch rounded-xl px-1 hover:bg-gray-200"
      buttonContent={
        <div title={accountButtonContent} aria-label={accountButtonContent}>
          <AccountInitials name={accountButtonContent} />
        </div>
      }
      dropdownContent={
        <div className="p-2">
          <div className="flex items-center gap-3 px-3 py-2">
            <UserCircle className="h-6 w-6 shrink-0 text-neutral-500" />

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-primary">
                {accountButtonContent}
              </p>
              {email && (
                <p className="truncate text-xs font-light text-neutral-500">
                  {email}
                </p>
              )}
            </div>

            {hasAccountSettings && (
              <button className={settingsButtonClassName} onClick={onSettings}>
                Settings
              </button>
            )}
          </div>

          <div className="mx-2 my-1 border-b border-gray-200" />

          {project && (
            <>
              <div className="flex items-center gap-3 px-3 py-2">
                {/* The project icon has a green fill baked in, so override it
                    to match the other grey menu icons. */}
                <ProjectSvg className="h-6 w-6 shrink-0 text-neutral-500 [&_path]:fill-current" />

                <div className="min-w-0 flex-1">
                  {projectRoleLabel && (
                    <p className="truncate text-sm text-primary">
                      {projectRoleLabel}
                    </p>
                  )}
                  <p className="truncate text-xs font-light text-neutral-500">
                    {project.name}
                  </p>
                </div>

                <button
                  className={settingsButtonClassName}
                  onClick={onProjectSettings}
                >
                  Settings
                </button>
              </div>

              <div className="mx-2 my-1 border-b border-gray-200" />
            </>
          )}

          <button className={rowClassName} onClick={onContactSupport}>
            <SupportSvg className="h-6 w-6 shrink-0 text-neutral-500" />
            <span className="flex-1">Contact support</span>
            <ExternalLinkSvg className="h-5 w-5 shrink-0 text-neutral-300" />
          </button>

          <div className="mx-2 my-1 border-b border-gray-200" />

          <form method="post" action="/api/auth/logout">
            <button type="submit" className={rowClassName}>
              <LogoutSvg className="h-6 w-6 shrink-0 text-neutral-500" />
              <span className="flex-1">Log out</span>
            </button>
          </form>
        </div>
      }
    />
  );
};

// Proposed look for the header: two initials in a light circle, matching the
// MemberAvatar restyle in PR 2578. Swap for MemberAvatar once that merges.
function AccountInitials(p: { name: string }) {
  const words = (p.name ?? "").trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? "";
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : "";
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-neutral-100 text-xs text-neutral-900">
      {(first + last).toUpperCase()}
    </div>
  );
}

export default AccountDropdown;
